/* WHERE THE DATA COMES FROM — the one file that knows.
 *
 * Before this, six stores each held their own `const MARINE =
 * '/signalk/marine-data'` and four more built paths off
 * `import.meta.env.BASE_URL`. Every one of those is a statement that the chart
 * is running as a webapp on a SignalK server on a boat, written in a file whose
 * subject is tides or fish. Hosting the same chart anywhere else meant editing
 * ten files, and the layers could not say WHY they had no data because they had
 * no way to know a host simply does not have that service.
 *
 * ═══ FOUR SOURCES, AND THEY ARE NOT THE SAME KIND OF THING ═══
 *
 *   static-data   Files shipped WITH the app — a year of tides, the EC zone
 *                 polygons, the DFO subareas, the costgrid. Always present,
 *                 because if the app loaded then so did these. Overridable all
 *                 the same, so they can be moved to object storage later.
 *
 *   marine-data   The pi plugin's live routes: wind observations, forecasts,
 *                 the NEMO current and surface fields, DFO notices. This is the
 *                 one a hosted chart may genuinely not have, and the one whose
 *                 absence has to read as "no provider" rather than as an error.
 *
 *   chart-cache   The tile proxy. Absent means "go direct to CHS", which is
 *                 already a supported path — see map/style.ts.
 *
 *   signalk       The server itself: history, resources, the delta stream.
 *                 Absent on any host that is not a boat, which is what makes
 *                 the own-vessel and route layers a `boat` pack.
 *
 *   feeds         services/layer-cache's feed API — `/feeds` to discover what
 *                 this host can answer, `/feed/{id}` to ask. DISTINCT from
 *                 `marine-data` on purpose: that one means the pi's route
 *                 names, and pointing it at the layer cache made two layers
 *                 say "waiting for the field from the pi…" on a host with no
 *                 pi. Two shapes, two names, until the pi serves this one too.
 *
 * ═══ ABSENT IS A FACT, NOT A FAILURE ═══
 *
 * `has()` is the whole point. A layer that declares `requires: ['marine-data']`
 * and finds no provider greys out in the panel with a reason, through exactly
 * the machinery that already handles a provider going offline — see
 * LayerManager's availability handling, which has always kept "can this work"
 * separate from "did the user ask for it".
 *
 * ═══ ONE INSTANCE, NOT A FACTORY ARGUMENT ═══
 *
 * The stores are module singletons and a page never shows two charts, so this
 * is configured once at boot rather than threaded through forty call sites.
 * When that stops being true, `configureSources` is the seam to change.
 */

export type SourceName = 'static-data' | 'marine-data' | 'chart-cache' | 'signalk' | 'feeds';

export interface ChartSources {
  /** False when this host has no such service. Never throws. */
  has(name: SourceName): boolean;
  /** Base URL, no trailing slash. Throws when the source is absent — call `has` first. */
  base(name: SourceName): string;
  /** `base(name)` joined to `path`, which may start with or without a slash. */
  url(name: SourceName, path: string): string;
  /** `fetch` against `url(name, path)`. Throws the same way when absent. */
  fetch(name: SourceName, path: string, init?: RequestInit): Promise<Response>;
}

/** Thrown by `base`/`url`/`fetch` for a source this host does not provide. */
export class SourceUnavailable extends Error {
  constructor(readonly source: SourceName) {
    super(`no provider configured for "${source}"`);
    this.name = 'SourceUnavailable';
  }
}

const trimEnd = (s: string) => s.replace(/\/+$/, '');

export function chartSources(bases: Partial<Record<SourceName, string | null>>): ChartSources {
  const resolved = new Map<SourceName, string>();
  for (const [k, v] of Object.entries(bases)) {
    if (typeof v === 'string') resolved.set(k as SourceName, trimEnd(v));
  }
  const self: ChartSources = {
    has: (n) => resolved.has(n),
    base(n) {
      const b = resolved.get(n);
      if (b === undefined) throw new SourceUnavailable(n);
      return b;
    },
    url: (n, path) => `${self.base(n)}/${path.replace(/^\/+/, '')}`,
    fetch: (n, path, init) => fetch(self.url(n, path), init)
  };
  return self;
}

/**
 * What a SignalK webapp on a boat looks like — and what everything did before
 * this file existed, so configuring nothing changes nothing.
 *
 * `appBase` is the public path the app is served from (Vite's `BASE_URL`,
 * `/gennetay-dash/` here). It is only used to find the shipped `data/`
 * directory, which really does travel with the build.
 */
export function signalkSources(
  appBase: string,
  /* Only `feeds`, and only because `probeLayerCache` has to rebuild the whole
   * set to change one entry. A general override parameter would invite hosts to
   * assemble their own boat, which is what `chartSources` is for. */
  found?: { feeds?: string }
): ChartSources {
  return chartSources({
    'static-data': `${trimEnd(appBase)}/data`,
    'marine-data': '/signalk/marine-data',
    'chart-cache': '/signalk/chart-cache',
    signalk: '/signalk',
    /* ═══ NULL UNTIL THE PLUGIN IS PROVED TO BE THERE ═══
     *
     * services/layer-cache mounts at LAYER_CACHE_ROUTE below, but only if
     * ops/deploy-layer-cache.sh has been run on this boat and the plugin
     * enabled. It cannot be assumed, and it must not be assumed OPTIMISTICALLY:
     * availability is `sources().has('feeds')`, which asks whether an endpoint
     * is configured and not whether it answers. Naming a path that 404s would
     * light up every feed-backed row and then fail behind it.
     *
     * That exact fault is on record — see the comment in apps/web's main.ts
     * about a layer that stopped saying "no provider on this host", which was
     * true and actionable, and started saying "waiting for the field…" on a
     * host that had none.
     *
     * So the boat PROBES. `probeLayerCache` below is called once at boot and
     * reconfigures only on a real answer. Absent is the honest default and
     * stays the default on a pi that has not been deployed to. */
    feeds: found?.feeds ?? null
  });
}

/** Where ops/deploy-layer-cache.sh mounts the plugin. */
export const LAYER_CACHE_ROUTE = '/signalk/layer-cache';

/**
 * Ask the boat whether it has a layer cache, and wire it up if it does.
 *
 * ═══ WHY A PROBE AND NOT A SETTING ═══
 *
 * The alternative is a checkbox somebody has to tick after deploying, which is
 * a second place for the truth to live and the place it would go stale. The
 * plugin either answers its catalogue or it does not, and that is already the
 * fact we want.
 *
 * Cheap and safe to lose: one small GET, a short timeout, no retry. A boat with
 * no plugin gets a 404 and keeps the honest `null`; a boat whose server is busy
 * gets a timeout and the same, and the layers say what they said before.
 * Nothing waits on this — it resolves into `configureSources` whenever it
 * lands, and the layer rows are reactive to it.
 *
 * @returns whether the layer cache was found and configured.
 */
export async function probeLayerCache(
  appBase: string,
  configure: (s: ChartSources) => void,
  route: string = LAYER_CACHE_ROUTE
): Promise<boolean> {
  try {
    const r = await fetch(`${route}/feeds`, { signal: AbortSignal.timeout(4000) });
    if (!r.ok) return false;
    /* A 200 IS NOT ENOUGH. A SignalK webapp is served by an express static
     * mount with an SPA fallback, so a path that matches no route can come back
     * as somebody's index.html with a cheerful 200. The catalogue's SHAPE is
     * the proof that a layer cache answered, and it costs one `Array.isArray`.
     * This is the same lesson as the chart cache's blank-tile rule: a 200 says
     * a server replied, not that it replied with what was asked for. */
    const j = (await r.json()) as { ok?: boolean; feeds?: unknown[] };
    if (j.ok !== true || !Array.isArray(j.feeds)) return false;
    configure(signalkSources(appBase, { feeds: route }));
    return true;
  } catch {
    /* Offline, timed out, 404, or HTML that would not parse. Every one of them
     * means the same thing here: this boat has no layer cache, which is the
     * state the app already knows how to render. */
    return false;
  }
}

/* The default is the boat, so nothing had to be configured for the dash to keep
 * working through this change — and so a test or a proof that never calls
 * `configureSources` behaves exactly as it did. apps/web overrides it at boot. */
let current: ChartSources = signalkSources(
  typeof import.meta.env === 'object' ? (import.meta.env.BASE_URL ?? '/') : '/'
);

/** Call once, before the map is built. */
export function configureSources(next: ChartSources): void {
  current = next;
}

/** The configured sources. Read per call — never cache the result in a module
 *  constant, which is the habit this file exists to break. */
export function sources(): ChartSources {
  return current;
}
